/* eslint-disable */

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { colors } from '../../helper/constant/colors';
import { useShopSearch } from '../../hooks/useShopSearch';
import './MobileSearchOverlay.css';

function MobileSearchOverlay({
  isOpen,
  onClose,
  permitdata,
  app_hostname,
  productCatalog,
  islogin,
}) {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [filtertext, setFiltertext] = useState('');
  const [debouncedText, setDebouncedText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [navigate_url, setNavigate_url] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 350);
    }
    if (!isOpen) {
      setFiltertext('');
    }
  }, [isOpen]);

  // Load search history from localStorage
  useEffect(() => {
    if (isOpen) {
      const history = localStorage.getItem('meya_recent_searches');
      if (history) {
        try {
          setRecentSearches(JSON.parse(history));
        } catch (e) {
          setRecentSearches([]);
        }
      }
    }
  }, [isOpen]);

  const addRecentSearch = (text) => {
    if (!text || text.trim() === '') return;
    const cleanText = text.trim();
    const updated = [cleanText, ...recentSearches.filter((item) => item !== cleanText)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('meya_recent_searches', JSON.stringify(updated));
  };

  const removeRecentSearch = (e, text) => {
    e.stopPropagation();
    const updated = recentSearches.filter((item) => item !== text);
    setRecentSearches(updated);
    localStorage.setItem('meya_recent_searches', JSON.stringify(updated));
  };

  const clearAllRecentSearches = (e) => {
    e.stopPropagation();
    setRecentSearches([]);
    localStorage.removeItem('meya_recent_searches');
  };

  // Debouncing Input Search
  useEffect(() => {
    if (filtertext === '') {
      setDebouncedText('');
      setIsLoading(false);
      if (productCatalog?.clear) {
        productCatalog.clear();
      }
      return;
    }
    setIsLoading(true);
    const handler = setTimeout(() => {
      setDebouncedText(filtertext);
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(handler);
  }, [filtertext]);

  useEffect(() => {
    if (!debouncedText) return;

    // Trigger server-side product search via productCatalog hook
    if (productCatalog?.search) {
      productCatalog.search(debouncedText, 'buyer_category');
    }
  }, [debouncedText, productCatalog?.search]);

  // Search for shop by name (search-as-you-type)
  const apiMatchedShop = useShopSearch(debouncedText);

  useEffect(() => {
    if (navigate_url !== '') {
      onClose();
      navigate(navigate_url);
      setNavigate_url('');
    }
  }, [navigate_url]);

  // Extract unique shops from productCatalog
  const uniqueShops = useMemo(() => {
    try {
      if (!productCatalog?.products) return [];
      const seen = new Set();
      const shops = [];
      productCatalog.products.forEach((item) => {
        if (item && item.shop_id && !seen.has(item.shop_id)) {
          seen.add(item.shop_id);
          shops.push({
            shop_id: item.shop_id,
            shop_name: item.shop_name || 'ร้านขายยาไม่ระบุชื่อ',
            shop_province: item.shop_province || 'กรุงเทพมหานคร',
          });
        }
      });
      return shops;
    } catch (e) {
      console.error('Error in uniqueShops useMemo:', e);
      return [];
    }
  }, [productCatalog?.products]);

  // Normalize helper: remove spaces/special chars, lowercase
  const norm = (s) =>
    String(s || '')
      .replace(/[^a-zA-Z0-9\u0E00-\u0E7F]/g, '')
      .toLowerCase();

  // Filter lists based on debouncedText
  const filteredShops = useMemo(() => {
    try {
      if (!debouncedText) return [];
      const searchWord = debouncedText.toLowerCase();
      const searchWordNorm = norm(debouncedText);
      const queryTokens = searchWord.split(/\s+/).filter(Boolean);
      const queryNormTokens = searchWordNorm.split(/\s+/).filter(Boolean);

      let localFiltered = uniqueShops.filter((shop) => {
        const name = shop && shop.shop_name ? String(shop.shop_name).toLowerCase() : '';
        const province = shop && shop.shop_province ? String(shop.shop_province).toLowerCase() : '';

        // 1. Direct substring match
        if (name.includes(searchWord) || province.includes(searchWord)) return true;

        // 2. Normalized substring match
        const nameNorm = norm(name);
        const provinceNorm = norm(province);
        if (nameNorm.includes(searchWordNorm) || provinceNorm.includes(searchWordNorm)) return true;

        // 3. Token-based match (all search terms must match somewhere in name or province)
        if (queryTokens.length > 1) {
          const allTokensMatch = queryTokens.every(
            (token) => name.includes(token) || province.includes(token),
          );
          if (allTokensMatch) return true;
        }

        // 4. Normalized token-based match
        if (queryNormTokens.length > 0) {
          const allNormTokensMatch = queryNormTokens.every(
            (token) => nameNorm.includes(token) || provinceNorm.includes(token),
          );
          if (allNormTokensMatch) return true;
        }

        return false;
      });
      if (apiMatchedShop) {
        const alreadyExists = localFiltered.some((s) => s.shop_id === apiMatchedShop.shop_id);
        if (!alreadyExists) {
          localFiltered = [apiMatchedShop, ...localFiltered];
        }
      }
      return localFiltered;
    } catch (e) {
      console.error('Error in filteredShops useMemo:', e);
      return [];
    }
  }, [debouncedText, uniqueShops, apiMatchedShop]);

  const handleLoadMore = () => {
    const nextPage = (productCatalog?.page || 1) + 1;
    setIsLoadingMore(true);
    // Only use loadPage to fetch the next page - avoid resetting with search()
    if (productCatalog?.loadPage) {
      productCatalog.loadPage(nextPage);
    }
  };

  // Stop loading when products arrive
  useEffect(() => {
    if (isLoadingMore && !productCatalog?.loading) {
      setIsLoadingMore(false);
    }
  }, [productCatalog?.loading, isLoadingMore]);

  // Use accumulated results (for load more support)
  const filteredProducts = useMemo(() => {
    try {
      return productCatalog?.products || [];
    } catch (e) {
      console.error('Error in filteredProducts useMemo:', e);
      return [];
    }
  }, [productCatalog?.products]);

  const popularKeywords = [
    'พาราเซตามอล',
    'ยาแก้ไอ',
    'วิตามินซี',
    'หน้ากากอนามัย 3D',
    'ยาหยอดตา',
    'ฟ้าทะลายโจร',
  ];

  const suggestedShops = useMemo(() => {
    try {
      return uniqueShops.slice(0, 3);
    } catch (e) {
      return [];
    }
  }, [uniqueShops]);

  const suggestedProducts = useMemo(() => {
    try {
      if (!productCatalog?.products) return [];
      return productCatalog.products.slice(0, 3);
    } catch (e) {
      return [];
    }
  }, [productCatalog?.products]);

  function begin_search(mode, type, id, detail, customKeyword) {
    const keyword = customKeyword !== undefined ? customKeyword : filtertext;
    if (keyword !== '') {
      addRecentSearch(keyword);
      if (mode === 'shop') {
        getShopID(keyword);
      } else if (mode === 'product') {
        if (type === 'contain') {
          // Pre-check: local normalized shop name match
          const keywordNorm = norm(keyword);
          if (keywordNorm) {
            // 1. First look for exact match
            let matchedShop = uniqueShops.find((shop) => norm(shop.shop_name) === keywordNorm);

            // 2. If no exact match, look for substring match (only if keyword is at least 3 characters to avoid false positives on short words like 'ยา')
            if (!matchedShop && keywordNorm.length >= 3) {
              matchedShop = uniqueShops.find((shop) => norm(shop.shop_name).includes(keywordNorm));
            }

            if (matchedShop) {
              setNavigate_url(
                '/marketplace/shopseller?shopcode=' + matchedShop.shop_id + '&category=รวมสินค้า',
              );
              setFiltertext('');
              return;
            }
          }

          const formdata = new FormData();
          formdata.append('shop_text', keyword);
          const headers = { Accept: 'application/json' };
          if (permitdata && permitdata.token) {
            headers.Authorization = 'Bearer ' + permitdata.token;
          }
          axios
            .post(app_hostname + '/api/market/help/getshopidbyname', formdata, { headers })
            .then((response) => {
              if (
                response.data.status === 'OK' &&
                Array.isArray(response.data.shop_info) &&
                response.data.shop_info.length > 0
              ) {
                setNavigate_url(
                  '/marketplace/shopseller?shopcode=' +
                    response.data.shop_info[0].shop_id +
                    '&category=รวมสินค้า',
                );
              } else {
                setNavigate_url(
                  '/marketplace?category=ค้นหาสินค้า&searchtype=contain&productid=none&detail=' +
                    keyword,
                );
              }
            })
            .catch(() => {
              setNavigate_url(
                '/marketplace?category=ค้นหาสินค้า&searchtype=contain&productid=none&detail=' +
                  keyword,
              );
            });
        } else if (type === 'productid') {
          setNavigate_url(
            '/marketplace?category=ค้นหาสินค้า&searchtype=productid&productid=' +
              id +
              '&detail=' +
              keyword,
          );
        }
      }
      setFiltertext('');
    }
  }

  function getShopID(text) {
    const formdata = new FormData();
    formdata.append('shop_text', text);
    const headers = { Accept: 'application/json' };
    if (permitdata && permitdata.token) {
      headers.Authorization = 'Bearer ' + permitdata.token;
    }
    axios
      .post(app_hostname + '/api/market/help/getshopidbyname', formdata, { headers })
      .then((response) => {
        if (response.data.status === 'OK') {
          if (Array.isArray(response.data.shop_info) && response.data.shop_info.length > 0) {
            setNavigate_url(
              '/marketplace/shopseller?shopcode=' +
                response.data.shop_info[0].shop_id +
                '&category=รวมสินค้า',
            );
            setFiltertext('');
          } else {
            alert('ไม่พบร้านค้าที่ท่านต้องการค้นหา กรุณาลองใหม่อีกครั้ง');
          }
        } else {
          alert(response.data.message || 'เกิดข้อผิดพลาดในการค้นหาร้านค้า');
        }
      })
      .catch(() => {
        alert('เกิดข้อผิดพลาดทางเทคนิค กรุณาลองใหม่อีกครั้ง');
      });
  }

  const handleSelectOption = (item) => {
    if (item.type === 'shop') {
      addRecentSearch(item.name);
      setNavigate_url('/marketplace/shopseller?shopcode=' + item.id + '&category=รวมสินค้า');
    } else if (item.type === 'product') {
      addRecentSearch(item.name);
      setNavigate_url(
        '/marketplace?category=ค้นหาสินค้า&searchtype=productid&productid=' +
          item.id +
          '&detail=' +
          item.name,
      );
    } else if (item.type === 'all') {
      addRecentSearch(item.keyword);
      begin_search('product', 'contain', null, null, item.keyword);
    }
  };

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      begin_search('product', 'contain');
    }
  }

  return (
    <div className={'mobile-search-overlay' + (isOpen ? ' open' : '')}>
      <div className='mobile-search-overlay__header'>
        <button
          className='mobile-search-overlay__back-btn'
          onClick={onClose}
          aria-label='ปิดการค้นหา'
        >
          <i className='fas fa-arrow-left'></i>
        </button>
        <div className='mobile-search-overlay__input-wrap'>
          <i className='fas fa-search mobile-search-overlay__input-icon'></i>
          <input
            ref={inputRef}
            type='search'
            className='mobile-search-overlay__input font-th'
            placeholder='ค้นหาสินค้าและร้านขายยา'
            value={filtertext}
            onChange={(e) => setFiltertext(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {filtertext !== '' && (
            <button
              className='mobile-search-overlay__clear-btn'
              onClick={() => setFiltertext('')}
              aria-label='ล้างข้อความ'
            >
              <i className='fas fa-times'></i>
            </button>
          )}
        </div>
      </div>

      <div className='mobile-search-overlay__body'>
        {isLoading ? (
          <div className='mobile-search-overlay__loading'>
            <div className='search-skeleton-item'>
              <div className='search-skeleton-circle'></div>
              <div className='search-skeleton-bar'></div>
            </div>
            <div className='search-skeleton-item'>
              <div className='search-skeleton-circle'></div>
              <div className='search-skeleton-bar'></div>
            </div>
            <div className='search-skeleton-item'>
              <div className='search-skeleton-circle'></div>
              <div className='search-skeleton-bar'></div>
            </div>
          </div>
        ) : filtertext === '' ? (
          <div className='mobile-search-overlay__suggestions'>
            {/* 🕘 Recent Searches */}
            {recentSearches.length > 0 && (
              <div className='search-section mb-4'>
                <div className='d-flex align-items-center justify-content-between mb-2'>
                  <div className='search-section-title mb-0 border-0'>🕘 ค้นหาล่าสุด</div>
                  <button
                    type='button'
                    className='btn btn-link text-muted font-size-12 p-0'
                    onClick={clearAllRecentSearches}
                    style={{ textDecoration: 'none', border: 'none', background: 'transparent' }}
                  >
                    ล้างทั้งหมด
                  </button>
                </div>
                <div className='search-pills-wrap'>
                  {recentSearches.map((phrase, idx) => (
                    <div
                      key={idx}
                      className='search-pill history-pill'
                      onClick={() => {
                        setFiltertext(phrase);
                      }}
                    >
                      <span className='pill-text'>{phrase}</span>
                      <span className='pill-remove' onClick={(e) => removeRecentSearch(e, phrase)}>
                        &times;
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 🔥 Popular searches */}
            <div className='search-section mb-4'>
              <div className='search-section-title'>🔥 คำค้นหายอดนิยม</div>
              <div className='search-pills-wrap'>
                {popularKeywords.map((keyword, idx) => (
                  <div
                    key={idx}
                    className='search-pill popular'
                    onClick={() => {
                      setFiltertext(keyword);
                    }}
                  >
                    <i className='fas fa-fire text-warning mr-1'></i> {keyword}
                  </div>
                ))}
              </div>
            </div>

            {/* 🏪 Shop Recommendations */}
            {suggestedShops.length > 0 && (
              <div className='search-section mb-4'>
                <div className='search-section-title'>🏪 ร้านขายยาแนะนำ</div>
                <div className='search-pills-wrap'>
                  {suggestedShops.map((shop, idx) => (
                    <div
                      key={idx}
                      className='search-pill shop-pill'
                      onClick={() =>
                        handleSelectOption({ type: 'shop', id: shop.shop_id, name: shop.shop_name })
                      }
                    >
                      <i className='fas fa-store text-primary mr-1'></i> {shop.shop_name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : filteredShops.length === 0 && filteredProducts.length === 0 ? (
          <div className='mobile-search-overlay__empty-state'>
            <div className='search-empty-icon'>
              <i className='fas fa-search'></i>
            </div>
            <h5 className='search-empty-title'>ไม่พบสินค้าหรือร้านค้า</h5>
            <p className='search-empty-desc'>
              ขออภัยด้วยค่ะ ลองค้นหาใหม่อีกครั้งโดยใช้คำแนะนำด้านล่างนี้นะคะ
            </p>
            <div className='search-empty-actions'>
              <button
                className='search-empty-btn primary'
                onClick={() => {
                  setFiltertext('');
                  onClose();
                  navigate('/marketplace');
                }}
              >
                ดูสินค้าทั้งหมด
              </button>
            </div>
            <div className='search-pills-wrap mt-3 justify-content-center'>
              {popularKeywords.slice(0, 3).map((keyword, idx) => (
                <div
                  key={idx}
                  className='search-pill'
                  onClick={() => {
                    setFiltertext(keyword);
                  }}
                >
                  {keyword}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className='mobile-search-results'>
            {/* Shops */}
            {filteredShops.length > 0 && (
              <div className='search-section mb-3'>
                <div className='search-section-title'>🏪 ร้านขายยา ({filteredShops.length})</div>
                {filteredShops.map((shop, index) => (
                  <div
                    key={index}
                    className='mobile-search-item'
                    onClick={() =>
                      handleSelectOption({ type: 'shop', id: shop.shop_id, name: shop.shop_name })
                    }
                  >
                    <div className='item-avatar'>
                      <i className='fas fa-store-alt'></i>
                    </div>
                    <div className='item-details'>
                      <span className='item-name'>{shop.shop_name}</span>
                    </div>
                    <span className='item-meta'>
                      <i className='fas fa-map-marker-alt mr-1'></i> {shop.shop_province}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Products */}
            {filteredProducts.length > 0 && (
              <div className='search-section mb-3'>
                <div className='search-section-title'>💊 สินค้า ({filteredProducts.length})</div>
                {filteredProducts.map((item, index) => (
                  <div
                    key={index}
                    className='mobile-search-item'
                    onClick={() =>
                      handleSelectOption({
                        type: 'product',
                        shopitem_id: item.shopitem_id,
                        id: item.productitem_id,
                        name: item.productitem_name,
                      })
                    }
                  >
                    {item.img1_key && item.productitem_id ? (
                      <div className='item-thumbnail'>
                        <img
                          src={`${process.env.REACT_APP_IMAGE_URL}${item.productitem_id}/${item.productitem_id}-1-${item.img1_key}.jpg`}
                          alt={item.productitem_name}
                          onError={(e) => {
                            e.currentTarget.src = require('../../assets/images/placeholder.webp');
                          }}
                        />
                      </div>
                    ) : (
                      <div className='item-avatar'>
                        <i className='fas fa-capsules'></i>
                      </div>
                    )}
                    <div className='item-details'>
                      <span className='item-name'>{item.productitem_name}</span>
                      {item.productitem_inter && (
                        <span className='item-sub'>{item.productitem_inter}</span>
                      )}
                    </div>
                  </div>
                ))}
                {(productCatalog?.total || 0) > (productCatalog?.products?.length || 0) && (
                  <button
                    className='mobile-search-item'
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                      opacity: isLoadingMore ? 0.6 : 1,
                      marginTop: '8px',
                    }}
                  >
                    {isLoadingMore ? (
                      <>
                        <i className='fas fa-spinner fa-spin mr-2'></i>
                        <span style={{ fontWeight: '600', color: '#2196F3' }}>กำลังโหลด...</span>
                      </>
                    ) : (
                      <>
                        <i className='fas fa-chevron-down mr-2'></i>
                        <span style={{ fontWeight: '600', color: '#2196F3' }}>
                          โหลดเพิ่มเติม ({productCatalog?.products?.length || 0}/
                          {productCatalog?.total || 0})
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Literal Search */}
            <div
              className='mobile-search-item search-all-item'
              style={{
                borderTop: `1px solid ${colors.slate200}`,
                marginTop: '12px',
                paddingTop: '12px',
              }}
              onClick={() => handleSelectOption({ type: 'all', keyword: debouncedText })}
            >
              <i className='fas fa-search text-primary mr-2'></i>
              <span className='item-name' style={{ fontWeight: '700' }}>
                ค้นหา "{debouncedText}" ในสินค้าทั้งหมด
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MobileSearchOverlay;
